import React from 'react';
import {
  Box,
  Button,
  ButtonText,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type { OralExamQuestion } from '@/common/api/models/oralExamQuestion';
import { useDialog } from '@/common/context/DialogContext';
import { EditableQuestionItem, type TaskProvenanceMeta } from './EditableQuestionItem';

interface EditableQuestionPoolProps {
  questions: OralExamQuestion[];
  onChange: (pool: OralExamQuestion[]) => void;
  generatedAt?: string | null;
  lastEditedAt?: string | null;
  isReadOnly?: boolean;
  isGenerating?: boolean;
  taskMetaById: Readonly<Record<string, TaskProvenanceMeta>>;
  resourceTitleById: Readonly<Record<string, string>>;
}

export const EditableQuestionPool: React.FC<EditableQuestionPoolProps> = ({
  questions,
  onChange,
  generatedAt,
  lastEditedAt,
  isReadOnly = false,
  isGenerating = false,
  taskMetaById,
  resourceTitleById,
}) => {
  const { t } = useTranslation();
  const { showDialog } = useDialog();

  const updateQuestion = (idx: number, updated: OralExamQuestion) => {
    onChange(questions.map((q, i) => (i === idx ? updated : q)));
  };

  const deleteQuestion = (idx: number) => {
    onChange(questions.filter((_, i) => i !== idx));
  };

  const confirmDeleteQuestion = (idx: number) => {
    showDialog({
      title: t('lecturer.oralExamConfig.deleteQuestionTitle'),
      message: t('lecturer.oralExamConfig.deleteQuestionMessage'),
      buttons: [
        {
          text: t('common.buttons.cancel'),
          variant: 'outline',
          action: 'secondary',
          onPress: () => {},
        },
        {
          text: t('common.buttons.delete'),
          action: 'negative',
          onPress: () => deleteQuestion(idx),
        },
      ],
    });
  };

  const addQuestion = () => {
    onChange([
      ...questions,
      {
        question: '',
        referringTaskIds: [],
        referringResourceIds: [],
        answerKey: [],
        concept: null,
        bloom: null,
        estTimeSec: null,
      },
    ]);
  };

  // Show the most recent relevant timestamp: edited > generated. Only one label at a time.
  const metadataLabel = (() => {
    if (lastEditedAt) {
      return t('lecturer.oralExamConfig.poolLastEditedAt', { date: new Date(lastEditedAt).toLocaleString() });
    }
    if (generatedAt) {
      return t('lecturer.oralExamConfig.poolGeneratedAt', { date: new Date(generatedAt).toLocaleString() });
    }
    return null;
  })();

  return (
    <VStack space="sm">
      {!!metadataLabel && (
        <Text size="2xs" color="$textHint">
          {metadataLabel}
        </Text>
      )}

      {questions.length === 0 && !isGenerating && (
        <Box py="$6" px="$3" bg="$bgSubtle" borderRadius="$md" alignItems="center">
          <Text size="sm" color="$textSubtle" textAlign="center" mb="$3">
            {t('lecturer.oralExamConfig.poolEmpty')}
          </Text>
          {!isReadOnly && (
            <Button
              size="sm"
              action="secondary"
              onPress={addQuestion}
              testID="oral-exam-add-question-button"
            >
              <ButtonText>
                {'+ '}
                {t('lecturer.oralExamConfig.addQuestion')}
              </ButtonText>
            </Button>
          )}
        </Box>
      )}

      {questions.map((question, idx) => (
        <EditableQuestionItem
          // stable enough key: question_id when persisted, fallback to index for new blanks
          key={question.questionId ?? `new-${idx}`}
          question={question}
          index={idx}
          onChange={(updated) => updateQuestion(idx, updated)}
          onDelete={() => confirmDeleteQuestion(idx)}
          isReadOnly={isReadOnly}
          taskMetaById={taskMetaById}
          resourceTitleById={resourceTitleById}
        />
      ))}

      {!isReadOnly && questions.length > 0 && (
        <Button
          size="sm"
          variant="outline"
          action="secondary"
          onPress={addQuestion}
          alignSelf="flex-start"
          testID="oral-exam-add-question-button"
        >
          <ButtonText>
            {'+ '}
            {t('lecturer.oralExamConfig.addQuestion')}
          </ButtonText>
        </Button>
      )}
    </VStack>
  );
};

export default EditableQuestionPool;
